import React, { useEffect, useState } from 'react';
import { getAllGames, addGame, getActiveGames, toggleGameStatus, syncGameToRedis } from '../../api/games';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import { useLoader } from '../../context/LoaderContext';

const GamesList = () => {
    const { showLoader, hideLoader } = useLoader();
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [newGame, setNewGame] = useState({ name: "", weight: 0, color: "#000000", icon: "" });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusProcessing, setStatusProcessing] = useState(null); // ID of the game being toggled
    const [redisProcessing, setRedisProcessing] = useState(null); // ID of the game being synced to Redis

    const fetchGames = async () => {
        showLoader();
        try {
            // Fetch both lists in parallel
            const [allGamesRes, activeGamesRes] = await Promise.all([
                getAllGames(),
                getActiveGames()
            ]);

            let allGamesData = [];
            let activeGamesData = [];

            // Process All Games Response
            if (Array.isArray(allGamesRes)) {
                allGamesData = allGamesRes;
            } else if (allGamesRes.data && Array.isArray(allGamesRes.data)) {
                allGamesData = allGamesRes.data;
            }

            // Process Active Games Response
            if (Array.isArray(activeGamesRes)) {
                activeGamesData = activeGamesRes;
            } else if (activeGamesRes.data && Array.isArray(activeGamesRes.data)) {
                activeGamesData = activeGamesRes.data;
            }

            // Merge: Set isActive true if game ID exists in activeGamesData
            const mergedGames = allGamesData.map(game => ({
                ...game,
                isActive: activeGamesData.some(activeGame => activeGame.id === game.id)
            }));

            // Sort by ID Ascending
            mergedGames.sort((a, b) => a.id - b.id);

            setGames(mergedGames);

        } catch (error) {
            console.error("Games fetch error:", error);
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchGames();
    }, []);

    const handleChange = (e) => {
        setNewGame({ ...newGame, [e.target.name]: e.target.value });
    };

    const handleToggleStatus = async (game) => {
        if (statusProcessing) return;

        const newStatus = !game.isActive;
        setStatusProcessing(game.id);

        try {
            await toggleGameStatus(game.id, newStatus);
            toast.success(`Oyun ${newStatus ? 'aktif' : 'pasif'} edildi!`);

            // Optimistic update
            setGames(prev => prev.map(g =>
                g.id === game.id ? { ...g, isActive: newStatus } : g
            ));

            // Optionally refetch to be sure
            fetchGames();

        } catch (error) {
            toast.error(error.message || "Durum değiştirilemedi.");
        } finally {
            setStatusProcessing(null);
        }
    };

    const handleSyncRedis = async (game) => {
        if (redisProcessing === game.id) return;

        // Extract key from screen name (e.g. HarfSecScreen -> HarfSec)
        let gameKey = game.screen ? game.screen.replace('Screen', '') : null;

        // Custom Mappings for API Controller Names
        if (gameKey === 'DortxDort') gameKey = 'FourxFour';
        if (gameKey === 'Anagram') gameKey = 'WordMatches';

        if (!gameKey) {
            toast.error("Oyun ekran kodu geçersiz!");
            return;
        }

        setRedisProcessing(game.id);
        const toastId = toast.loading(`${game.name} Redis'e aktarılıyor...`);

        try {
            const res = await syncGameToRedis(gameKey);
            toast.success(res.message || "Redis'e başarıyla aktarıldı!", { id: toastId });
        } catch (error) {
            toast.error(error.message || "Aktarım başarısız!", { id: toastId });
        } finally {
            setRedisProcessing(null);
        }
    };

    const handleAddGame = async () => {
        if (!newGame.name) {
            toast.error("Oyun adı zorunludur.");
            return;
        }
        setIsSubmitting(true);
        try {
            await addGame(newGame);
            toast.success("Oyun başarıyla eklendi!");
            setShowModal(false);
            setNewGame({ name: "", weight: 0, color: "#000000", icon: "" });
            fetchGames();
        } catch (error) {
            toast.error(error.message || "Ekleme başarısız.");
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <>
            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between">
                    <h6 className="text-lg fw-semibold mb-0">Oyunlar Listesi</h6>
                    <div className="d-flex align-items-center gap-3">
                        <span className="badge bg-primary-600 text-white px-12 py-6 radius-4">
                            Toplam: {games.length}
                        </span>
                        <button className="btn btn-primary-600 radius-8 px-20 py-11 d-flex align-items-center gap-2" onClick={() => setShowModal(true)}>
                            <Icon icon="ic:baseline-plus" className="text-xl" /> Oyun Ekle
                        </button>
                    </div>
                </div>
                <div className="card-body p-24">
                    <div className="table-responsive">
                        <table className="table bordered-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">ID</th>
                                    <th scope="col">Oyun Adı</th>
                                    <th scope="col">Ekran Kodu (Screen)</th>
                                    <th scope="col">Ağırlık (Weight)</th>
                                    <th scope="col">Renk & İkon</th>
                                    <th scope="col">Yayın Durumu</th>
                                    <th scope="col">Redis İşlemi</th>
                                </tr>
                            </thead>
                            <tbody>
                                {games.length > 0 ? (
                                    games.map((game) => (
                                        <tr key={game.id}>
                                            <td>
                                                <span className="text-secondary-light fw-semibold">#{game.id}</span>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="w-40-px h-40-px radius-circle d-flex justify-content-center align-items-center text-white text-xl" style={{ backgroundColor: game.color }}>
                                                        {game.icon && !game.icon.includes('/') ? (
                                                            <Icon icon={`mdi:${game.icon}`} onError={(e) => { e.target.style.display = 'none' }} />
                                                        ) : (
                                                            <span className="fw-bold">{game.name.charAt(0)}</span>
                                                        )}
                                                    </div>
                                                    <span className="text-md fw-medium text-secondary-light">{game.name}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="text-secondary-light">{game.screen || '-'}</span>
                                            </td>
                                            <td>
                                                <span className="badge bg-neutral-200 text-secondary-light border border-neutral-400 px-12 py-4 radius-4">
                                                    {game.weight}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="d-flex align-items-center gap-2">
                                                    <span className="w-24-px h-24-px radius-4 border" style={{ backgroundColor: game.color }}></span>
                                                    <span className="text-xs text-secondary-light">{game.color}</span>
                                                    <span className="text-xs text-secondary-light ms-2">({game.icon})</span>
                                                </div>
                                            </td>
                                            <td>
                                                <button
                                                    className={`btn btn-sm d-flex align-items-center gap-2 px-12 py-6 radius-8 transition-2 ${statusProcessing === game.id
                                                        ? 'bg-warning-focus text-warning-main cursor-not-allowed'
                                                        : game.isActive
                                                            ? 'bg-success-focus text-success-main border border-success-200 hover-bg-success-100'
                                                            : 'bg-neutral-200 text-neutral-600 border border-neutral-300 hover-bg-neutral-300'
                                                        }`}
                                                    onClick={() => handleToggleStatus(game)}
                                                    disabled={statusProcessing === game.id}
                                                >
                                                    {statusProcessing === game.id ? (
                                                        <>
                                                            <i className="ri-loader-4-line animate-spin text-lg"></i>
                                                            <span className="fw-medium">İşleniyor</span>
                                                        </>
                                                    ) : game.isActive ? (
                                                        <>
                                                            <i className="ri-checkbox-circle-fill text-xl"></i>
                                                            <span className="fw-semibold">Yayında</span>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="ri-close-circle-line text-xl opacity-50"></i>
                                                            <span className="fw-medium">Pasif</span>
                                                        </>
                                                    )}
                                                </button>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-outline-primary-600 btn-sm d-flex align-items-center gap-2 hover-bg-primary-50"
                                                    onClick={() => handleSyncRedis(game)}
                                                    disabled={redisProcessing === game.id}
                                                >
                                                    {redisProcessing === game.id ? (
                                                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                                                    ) : (
                                                        <Icon icon="mdi:database-import" className="text-lg" />
                                                    )}
                                                    Redis'e Aktar
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-24">
                                            <p className="text-secondary-light mb-0">Kayıtlı oyun bulunamadı.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            {/* Modal */}
            {showModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header">
                                <h5 className="modal-title">Yeni Oyun Ekle</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModal(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Oyun Adı</label>
                                    <input type="text" className="form-control" name="name" value={newGame.name} onChange={handleChange} placeholder="Örn: 4x4" />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Ağırlık (Weight)</label>
                                    <input type="number" className="form-control" name="weight" value={newGame.weight} onChange={handleChange} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Renk (Hex)</label>
                                    <input type="color" className="form-control form-control-color w-100" name="color" value={newGame.color} onChange={handleChange} title="Choose your color" />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">İkon (MDI Name)</label>
                                    <input type="text" className="form-control" name="icon" value={newGame.icon} onChange={handleChange} placeholder="Örn: grid, star, link" />
                                    <small className="text-muted">Material Design Icon ismi girin.</small>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>İptal</button>
                                <button type="button" className="btn btn-primary-600" onClick={handleAddGame} disabled={isSubmitting}>
                                    {isSubmitting ? 'Ekleniyor...' : 'Kaydet'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default GamesList;
